'use client';

import React, { useEffect, useState } from 'react';
import Card from '@/components/card/Card';
import ChipsWrapper from '@/components/chips/ChipsWrapper';
import { motion } from 'framer-motion';
import { HeadlineWrapper } from '@/components/headline-wrapper/HeadlineWrapper';
import { TeamCardWrapperProps } from '@/components/team-cards-wrapper/types';
import { fetchAllTeams } from '@/api/team';
import { renameDomProps } from '@/utils/sanitizeDomProps';
import { useTranslations } from '@/lib/locale-context';

const TeamCardWrapper: React.FC<TeamCardWrapperProps> = ({
  className,
  sectionBackgroundColor,
  headlineWrapper,
  cardAlignment = 'left',
  sectionId,
  ...rest
}) => {
  const [cards, setCards] = useState<any[]>([]);
  const { t } = useTranslations();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([
    t('team.card.all'),
  ]);

  useEffect(() => {
    const loadTeamCards = async () => {
      const teamData = await fetchAllTeams();

      const formattedTeamData = teamData.map((card: any) => {
        return {
          ...card,
          category: card.category ? t(`team.card.${card.category}`) : null,
        };
      });
      setCards(formattedTeamData);
    };

    loadTeamCards();
  }, []);

  const uniqueCategories = (() => {
    const keys = cards.map((card) => card.category).filter(Boolean);
    const unique = Array.from(new Set(keys));
    return [
      t('team.card.all'),
      ...unique.filter((key) => key !== t('team.card.all')),
    ];
  })();

  const handleCategorySelectionChange = (categories: string[]) => {
    setSelectedCategories(categories);
  };

  const filteredCards = cards
    .filter(
      (card) =>
        selectedCategories[0] === t('team.card.all') ||
        card.category === selectedCategories[0],
    )
    .map((card, index) => ({
      key: `card-${card.name}-${index}`,
      name: `${card.name} ${card.surname}`,
      position: card.position,
      positionEn: card.positionEn,
      address: card.address,
      addressEn: card.addressEn,
      image: card.image,
      email: card.email,
      linkedIn: card.linkedIn,
      overlay: card.overlay,
      category: card.category,
      showBio: card.showBio,
      links: card.showDetailInformation
        ? [
            {
              leftIcon: { name: 'mail' },
              title: card.email,
              href: `mailto:${card.email}`,
            },
            {
              leftIcon: { name: 'phone' },
              title: card.phoneNumber,
              href: `tel:${card.phoneNumber}`,
            },
            {
              leftIcon: { name: 'map-pin' },
              title: card.address,
              href: '#',
            },
          ]
        : [],
    }));

  const renamedProps = renameDomProps(rest);

  return (
    <div
      id={sectionId}
      className={`relative w-full py-12 ${sectionBackgroundColor ? `bg-${sectionBackgroundColor}` : ''}`}
      {...renamedProps}
    >
      <div className="card-wrapper container relative z-10 mx-auto flex flex-col gap-10 px-4 sm:px-0">
        {headlineWrapper && (
          <HeadlineWrapper {...headlineWrapper} position={cardAlignment} />
        )}

        {uniqueCategories.length > 0 && (
          <ChipsWrapper
            options={uniqueCategories}
            selectedOptions={selectedCategories}
            onSelectionChange={handleCategorySelectionChange}
            variant="inline-wrap"
            selectionMode="single"
          />
        )}

        <div className="grid grid-cols-6 gap-6 sm:grid-cols-12">
          {filteredCards.map((cardProps) => (
            <motion.div
              key={cardProps.key}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ ease: 'easeInOut', duration: 0.5 }}
              layout
              className="col-span-full flex sm:col-span-6 lg:col-span-4 xl:col-span-3"
            >
              <div className="team-cards-items card-item w-full self-stretch">
                <Card
                  variant="team"
                  title={cardProps.name}
                  image={cardProps.image}
                  email={cardProps.email}
                  linkedIn={cardProps.linkedIn}
                  category={cardProps.category}
                  showBio={cardProps.showBio}
                  overlay={cardProps.overlay}
                  links={cardProps.links as any}
                  position={cardProps.position}
                  positionEn={cardProps.positionEn}
                  address={cardProps.address}
                  addressEn={cardProps.addressEn}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(TeamCardWrapper);
